import { useEffect, useState } from 'react';
import type { HocuspocusProvider } from '@hocuspocus/provider';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { ArtifactAiPanel } from '@/components/artifacts/ArtifactAiPanel';
import { StorageDisabledBanner } from '@/components/artifacts/StorageDisabledBanner';
import { ArtifactActivityPanel } from '@/components/artifacts/detail/ArtifactActivityPanel';
import { ArtifactApplicabilityPanel } from '@/components/artifacts/detail/ArtifactApplicabilityPanel';
import { ArtifactBodyPanel } from '@/components/artifacts/detail/ArtifactBodyPanel';
import { ArtifactExternalRefsPanel } from '@/components/artifacts/detail/ArtifactExternalRefsPanel';
import { ArtifactGithubPublicationPanel } from '@/components/artifacts/detail/ArtifactGithubPublicationPanel';
import { ArtifactHeaderBar } from '@/components/artifacts/detail/ArtifactHeaderBar';
import { ArtifactLifecyclePanel } from '@/components/artifacts/detail/ArtifactLifecyclePanel';
import { ArtifactLinksPanel } from '@/components/artifacts/detail/ArtifactLinksPanel';
import { ArtifactSnapshotsPanel } from '@/components/artifacts/detail/ArtifactSnapshotsPanel';
import { ArtifactWorkItemLinksPanel } from '@/components/artifacts/detail/ArtifactWorkItemLinksPanel';
import { ArtifactWorkspaceLayout } from '@/components/artifacts/detail/ArtifactWorkspaceLayout';
import { CollaborationProvider } from '@/components/artifacts/detail/CollaborationContext';
import { CollapsibleIsland } from '@/components/artifacts/detail/CollapsibleIsland';
import { useArtifactAi } from '@/hooks/useArtifactAi';
import { useSession } from '@/hooks/useSession';
import { ArtifactStorageDisabledError, getArtifact, type Artifact } from '@/lib/api';

export function ArtifactDetailPage(): React.ReactElement {
  const { artifactId } = useParams<{ artifactId: string }>();
  const [provider, setProvider] = useState<HocuspocusProvider | null>(null);
  const [previewSuggestionId, setPreviewSuggestionId] = useState<string | null>(null);
  const { user: currentUser } = useSession();
  // Hoist AI state so the body-area draft-preview banner and the AI panel
  // share a single instance of mutations + suggestion cache.
  const ai = useArtifactAi(artifactId ?? '');

  // When a generate/suggest stream signals a freshly created suggestion, auto-open
  // it in the draft preview so the user transitions seamlessly from the live
  // activity overlay into the proposed document.
  useEffect(() => {
    if (ai.readySuggestionId === null) return;
    setPreviewSuggestionId(ai.readySuggestionId);
    ai.consumeReadySuggestionId();
  }, [ai.readySuggestionId, ai.consumeReadySuggestionId]);

  const aiActivityMode: 'generate' | 'suggest' | null =
    ai.streamingMode === 'generate' || ai.streamingMode === 'suggest' ? ai.streamingMode : null;

  const { data, isLoading, isError, error } = useQuery<Artifact>({
    queryKey: ['artifact', artifactId],
    queryFn: () => {
      if (!artifactId) throw new Error('Missing artifactId');
      return getArtifact(artifactId);
    },
    enabled: !!artifactId,
    retry: (failureCount, err) =>
      !(err instanceof ArtifactStorageDisabledError) && failureCount < 2,
  });

  if (isError && error instanceof ArtifactStorageDisabledError) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <StorageDisabledBanner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <div className="rounded-md bg-error/15 p-3 text-sm text-error">{error.message}</div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-text-secondary">
        Loading…
      </div>
    );
  }

  const leftIslands = (
    <>
      <CollapsibleIsland title="Linked artifacts" defaultOpen id="island-linked-artifacts">
        <ArtifactLinksPanel artifact={data} />
      </CollapsibleIsland>
      <CollapsibleIsland title="Applicability" defaultOpen id="island-applicability">
        <ArtifactApplicabilityPanel artifact={data} />
      </CollapsibleIsland>
      <CollapsibleIsland title="External references" id="island-external-refs">
        <ArtifactExternalRefsPanel artifact={data} />
      </CollapsibleIsland>
      <CollapsibleIsland title="Snapshots" id="island-snapshots">
        <ArtifactSnapshotsPanel artifact={data} />
      </CollapsibleIsland>
    </>
  );

  const centerContent = (
    <>
      <section
        aria-labelledby="body-heading"
        className="rounded-md border border-border bg-surface/40 p-3 shadow-sm"
      >
        <h2 id="body-heading" className="pb-2 text-sm font-semibold text-text-primary">
          Body
        </h2>
        <ArtifactBodyPanel
          artifact={data}
          onProviderChange={setProvider}
          draftSuggestion={
            ai.openSuggestions.find(s => s.id === previewSuggestionId) ??
            ai.recentlyResolvedSuggestions.find(s => s.id === previewSuggestionId) ??
            null
          }
          accepting={ai.applyingSuggestionId === previewSuggestionId}
          rejecting={ai.rejectingSuggestionId === previewSuggestionId}
          aiDisabled={ai.aiDisabled}
          onAcceptDraft={() => {
            if (previewSuggestionId === null) return;
            const id = previewSuggestionId;
            void ai
              .apply(id)
              .then(() => {
                setPreviewSuggestionId(null);
              })
              .catch(() => undefined);
          }}
          onRejectDraft={() => {
            if (previewSuggestionId === null) return;
            const id = previewSuggestionId;
            void ai
              .reject(id)
              .then(() => {
                setPreviewSuggestionId(null);
              })
              .catch(() => undefined);
          }}
          onCloseDraft={() => {
            setPreviewSuggestionId(null);
          }}
          aiActivityMode={aiActivityMode}
          aiActivity={aiActivityMode === null ? undefined : ai.generateActivity}
          aiActivityError={ai.generateError}
          onCancelAiActivity={ai.cancelGenerate}
        />
      </section>
      <section
        aria-labelledby="activity-heading"
        className="rounded-md border border-border bg-surface/40 p-3 shadow-sm"
      >
        <h2 id="activity-heading" className="pb-2 text-sm font-semibold text-text-primary">
          Activity
        </h2>
        <ArtifactActivityPanel artifact={data} />
      </section>
    </>
  );

  const rightIslands = (
    <>
      <CollapsibleIsland title="AI collaboration" defaultOpen id="island-ai-collaboration">
        <ArtifactAiPanel
          artifact={data}
          ai={ai}
          previewSuggestionId={previewSuggestionId}
          onOpenSuggestionPreview={setPreviewSuggestionId}
          hideHeader
        />
      </CollapsibleIsland>
      <CollapsibleIsland title="Lifecycle" id="island-lifecycle">
        <ArtifactLifecyclePanel artifact={data} />
      </CollapsibleIsland>
      <CollapsibleIsland title="GitHub work items" id="island-work-items">
        <ArtifactWorkItemLinksPanel artifact={data} currentUser={currentUser} />
      </CollapsibleIsland>
      <CollapsibleIsland title="GitHub publications" id="island-github-publications">
        <ArtifactGithubPublicationPanel artifact={data} currentUser={currentUser} />
      </CollapsibleIsland>
    </>
  );

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <ArtifactHeaderBar artifact={data} />
      <CollaborationProvider provider={provider} artifactId={data.id}>
        <ArtifactWorkspaceLayout
          leftIslands={leftIslands}
          centerContent={centerContent}
          rightIslands={rightIslands}
        />
      </CollaborationProvider>
    </div>
  );
}
